import { all, takeLatest, call, put, select, delay } from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import {
  getDraftSuccess,
  getDraftFailure,
  saveDraftSuccess,
  saveDraftFailure,
  discardDraftSuccess,
  discardDraftFailure,
  getGeocodeSuccess,
  getGeocodeFailure,
  queryRecentLayerExtentSuccess,
  setDesignActivity,
  setExcavationActivity,
  setWorkingOnBehalfOfList,
  setEarliestValidDate,
  getEnquiryEssentialsSuccess,
  getEnquiryEssentialsFailure,
  getAssetOwnerCountFailure,
  getAssetOwnerCountSuccess,
  lodgeNewEnquiryFailure,
  lodgeNewEnquirySuccess,
  uploadEnquiryShapeSuccess,
  uploadEnquiryShapeFailure,
  getDuplicateEnquiryFailure,
  getDuplicateEnquirySuccess,
  getLastEnquiryFailure,
  getLastEnquirySuccess,
  setPendingEquiriesAmount,
  getRestrictionFailure,
  getRestrictionSuccess,
  setBulkCreating,
  setBulkBatchTotal,
  setBulkBatchCreated,
  setSuccessEnquiryUserReference,
  setMapErrorForLodgeEnquiryFail,
  setFailedEnquiries,
  updateNewLodgedEquiries,
  checkShapeWithinTeamFailure,
  checkShapeWithinTeamSuccess
} from './actions';
import { LodgeEnquiryActionTypes, LodgeEnquiryState } from './types';
import {
  getGeocodeByCoordinate,
  queryFeatuerLayerExtent
} from 'services/map.service';
import {
  getAuthorities,
  getEssentials,
  createEnquiry
} from 'services/oncall.service';
import {
  getEssentials as getEssentialsSentinel,
  createEnquiry as createEnquirySentinel
} from 'services/sentinel.service';
import {
  getEnquiryDraft,
  saveEnquiryDraft,
  discardEnquiryDraft,
  bulkCreateEnquiry,
  getEnquiry,
  getEnquiryDetails,
  uploadEnquiriesShape,
  parseEnquiriesShape
} from 'services/enquiry.service';
import { getSystemJob } from 'services/system-job.service';
import { checkShapeWithinTeamBoundary } from 'services/team.service';
import { getAuthorities as getSentinelAuthorities } from 'services/sentinel.service';
import { uploadFileToS3 } from 'services/file.service';
import { getMyLastEnquiry, getSelfRestriction } from 'services/user.service';
import { getNewDate, convertShapesToMultiPolygon } from 'utilities/helper.util';
import { sortBy } from 'lodash';

const isSentinel = process.env.REACT_APP_ENQUIRY_SENTINEL === 'true';

function* handleGetGeocode(request: any) {
  const emptyErrorText =
    'Unable to get address from geocode API. Please retry.';
  try {
    const response: AxiosResponse = yield call(
      getGeocodeByCoordinate,
      request.payload.longitude,
      request.payload.latitude
    );
    if (Array.isArray(response?.data?.Results)) {
      const geocode = response.data.Results[0];
      if (geocode) {
        yield put(
          getGeocodeSuccess({
            geocode,
            longitude: request.payload.longitude,
            latitude: request.payload.latitude
          })
        );
      } else {
        yield put(
          getGeocodeFailure({
            geocode: emptyErrorText,
            longitude: request.payload.longitude,
            latitude: request.payload.latitude
          })
        );
      }
    } else {
      yield put(
        getGeocodeFailure({
          geocode: emptyErrorText,
          longitude: request.payload.longitude,
          latitude: request.payload.latitude
        })
      );
    }
  } catch (err) {
    put(
      getGeocodeFailure({
        geocode: emptyErrorText,
        longitude: request.payload.longitude,
        latitude: request.payload.latitude
      })
    );
  }
}

function* handleQueryRecentLayerExtent(request: any) {
  try {
    const response: { count: number; extent: any } = yield call(
      queryFeatuerLayerExtent,
      request.payload.layer
    );
    if (response?.count > 0) {
      yield put(queryRecentLayerExtentSuccess(response.extent));
    } else {
      yield put(queryRecentLayerExtentSuccess({ noRecord: true }));
    }
  } catch (err) {
    // do nothing
  }
}

function* handleGetEnquiryEssentials() {
  try {
    const response: AxiosResponse = yield call(getEssentials);
    if (Array.isArray(response?.data)) {
      const availableExcavationId = ['106', '107', '118', '124', '134', '145'];
      const availableDesignId = ['216', '235', '245', '247'];
      let activitiesDesign: any = [];
      let activitiesExcavation: any = [];
      let authorityTypeList: any = [];
      let authorityList: any = [];
      let earliestValidDate: string = '';
      const promises: any = [];
      const authorityId: any = [];
      response.data.forEach((d: any) => {
        if (Array.isArray(d?.GetEnquiryPurposeListResult?.EnquiryPurpose)) {
          const enquiryPurpose =
            d.GetEnquiryPurposeListResult.EnquiryPurpose.filter(
              (p: any) => p.Name !== 'Emergency'
            );
          enquiryPurpose.forEach((p: any) => {
            if (p.ID === '0') {
              activitiesExcavation =
                p.EnquiryCategories.EnquiryCategory.Activities.Activity.filter(
                  (a: any) => availableExcavationId.includes(a.ID)
                ).map((a: any) => ({ value: a.ID, label: a.Name }));
            } else if (p.ID === '1') {
              activitiesDesign =
                p.EnquiryCategories.EnquiryCategory.Activities.Activity.filter(
                  (a: any) => availableDesignId.includes(a.ID)
                ).map((a: any) => ({ value: a.ID, label: a.Name }));
            }
          });
        }
        if (
          Array.isArray(d?.GetWorkingOnBehalfOfListResult?.WorkingOnBehalfOf)
        ) {
          authorityTypeList =
            d.GetWorkingOnBehalfOfListResult.WorkingOnBehalfOf.map(
              (wobo: any) => ({ value: wobo.ID, label: wobo.Name })
            );
          d.GetWorkingOnBehalfOfListResult.WorkingOnBehalfOf.forEach(
            (wobo: any) => {
              if (wobo.IsAuthorityRequired === 'true') {
                authorityId.push(wobo.ID);
                promises.push(call(getAuthorities, wobo.ID));
              }
            }
          );
        }
        if (d?.CalculateEarliestValidEnquiryStartDateViaPurposeResult) {
          earliestValidDate = getNewDate(
            d.CalculateEarliestValidEnquiryStartDateViaPurposeResult
          )
            .utc()
            .add(1, 'days')
            .startOf('day')
            .format();
        }
      });

      const results: any[] = yield all(promises);
      results.forEach((response: any, inx: number) => {
        const data =
          response.data.GetWorkingOnBehalfOfAuthorityListResult.WorkingOnBehalfOfAuthority.map(
            (d: any) => ({
              value: d.ID,
              label: d.Name,
              authorityTypeId: authorityId[inx]
            })
          );

        authorityList = [...authorityList, ...data];
      });

      yield put(setDesignActivity(sortBy(activitiesDesign, ['label'])));
      yield put(setExcavationActivity(sortBy(activitiesExcavation, ['label'])));
      yield put(
        setWorkingOnBehalfOfList({
          authorityType: authorityTypeList,
          authority: authorityList
        })
      );
      yield put(setEarliestValidDate(earliestValidDate));
      yield put(getEnquiryEssentialsSuccess());
    } else {
      yield put(getEnquiryEssentialsFailure());
    }
  } catch (err) {
    put(getEnquiryEssentialsFailure());
  }
}

function* handleGetEnquiryEssentialsSentinel() {
  try {
    const response: AxiosResponse = yield call(getEssentialsSentinel);

    if (response?.data) {
      const activityTypeDesign = response.data.activityTypeDesign;
      const activityTypeExcavation = response.data.activityTypeExcavation;
      const earliestValidDate = response.data.earliestDate;

      const activitiesDesign = activityTypeDesign.map((design: any) => {
        return {
          label: design.label,
          value: design.code
        };
      });
      const activitiesExcavation = activityTypeExcavation.map(
        (excavation: any) => {
          return {
            label: excavation.label,
            value: excavation.code
          };
        }
      );

      yield put(setDesignActivity(sortBy(activitiesDesign, ['label'])));
      yield put(setExcavationActivity(sortBy(activitiesExcavation, ['label'])));
      yield put(setEarliestValidDate(earliestValidDate));
      yield put(getEnquiryEssentialsSuccess());
    } else {
      yield put(getEnquiryEssentialsFailure());
    }
  } catch (err) {
    put(getEnquiryEssentialsFailure());
  }
}

function* handleGetAssetOwnerCount(request: any) {
  try {
    const { shapes, orgId } = request.payload;
    const shape =
      shapes.length === 1 ? shapes[0] : convertShapesToMultiPolygon(shapes);

    const response: AxiosResponse = yield call(
      getSentinelAuthorities,
      shape,
      orgId
    );
    if (response?.data) {
      const count =
        response.data.localGoverment.length + response.data.utility.length;

      if (isSentinel) {
        const localGov = response.data.localGoverment;
        const utility = response.data.utility;
        const authorityTypeList: Array<any> = [
          { value: '1', label: 'Local government' },
          { value: '2', label: 'Utility' },
          { value: '3', label: 'Private' },
          { value: '4', label: 'Other' }
        ];
        const authorityList: Array<any> = [];

        localGov.forEach((gov: any) => {
          const g = {
            label: gov.name,
            value: gov.id,
            authorityTypeId: '1'
          };
          authorityList.push(g);
        });
        utility.forEach((util: any) => {
          const u = {
            label: util.name,
            value: util.id,
            authorityTypeId: '2'
          };
          authorityList.push(u);
        });

        yield put(
          setWorkingOnBehalfOfList({
            authorityType: authorityTypeList,
            authority: authorityList
          })
        );
      }
      yield put(getAssetOwnerCountSuccess(count));
    } else {
      yield put(getAssetOwnerCountFailure());
    }
  } catch (err) {
    yield put(getAssetOwnerCountFailure());
  }
}

function* handleGetDuplicateEnquiry(request: any) {
  try {
    const response: AxiosResponse = yield getEnquiryDetails(request.payload);
    if (response?.data) {
      yield put(getDuplicateEnquirySuccess(response.data));
    } else {
      yield put(getDuplicateEnquiryFailure('Unable to duplicate enquiry.'));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      getDuplicateEnquiryFailure(msg || 'Unable to duplicate enquiry.')
    );
  }
}

function* handleGetLastEnquiry() {
  try {
    const response: AxiosResponse = yield call(getMyLastEnquiry);
    if (response?.data) {
      yield put(getLastEnquirySuccess(response.data));
    } else {
      yield put(getLastEnquiryFailure('Unable to get last enquiry'));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(getLastEnquiryFailure(msg || 'Unable to get last enquiry'));
  }
}

function* handleLodgeNewEnquiry(request: any) {
  try {
    let completedEnquiries = [];
    let failedFinishRetried = [];
    let failedMapIds = [];
    let finishBulkCreate = false;
    let firstRound = true;
    let totalAmount = 1;
    let sharedInfo = {};
    if (Array.isArray(request.payload)) {
      totalAmount = request.payload.length;
      sharedInfo = {
        start_at: request.payload[0].CommencesOn,
        end_at: request.payload[0].CompletesOn,
        enquiry_at: request.payload[0].EnquiryDate
      };
      yield put(setBulkBatchTotal(totalAmount));
      yield put(setPendingEquiriesAmount(request.payload.length));
      yield put(setBulkCreating(true));
    }
    const response: AxiosResponse = yield call(
      Array.isArray(request.payload) ? bulkCreateEnquiry : createEnquiry,
      request.payload
    );
    if (response?.data) {
      if (response.data.system_job_id) {
        do {
          const state: LodgeEnquiryState = yield select();
          const loadedEnquiries = state.enquiries;
          const pendingAmount = state.pendingEnquiriesAmount || 0;
          if (pendingAmount <= 0) {
            finishBulkCreate = true;
            yield put(setSuccessEnquiryUserReference(completedEnquiries));
            yield put(setMapErrorForLodgeEnquiryFail(failedMapIds));
            yield put(setFailedEnquiries(failedFinishRetried));
          } else {
            if (!firstRound) {
              yield delay(2000);
            }
            firstRound = false;
            const jobResponse: AxiosResponse = yield call(
              getSystemJob,
              { type: 'BULK_CREATE_ENQUIRIES' },
              response.data.system_job_id
            );
            if (jobResponse?.data) {
              if (
                Array.isArray(jobResponse.data.EnquiriesCreateJobs) &&
                jobResponse.data.EnquiriesCreateJobs.length > 0
              ) {
                const loadedEnquiryIds = loadedEnquiries.map((e: any) => e.id);
                completedEnquiries =
                  jobResponse.data.EnquiriesCreateJobs.filter(
                    (e: any) => e.status === 'completed'
                  );
                const completedEnquiryIds = completedEnquiries.map(
                  (e: any) => e.details.enquiryId
                );
                const notloadedEnquiryIds = completedEnquiryIds.filter(
                  (id: number) => !loadedEnquiryIds.includes(id)
                );
                const enquiries: any[] = yield all(
                  notloadedEnquiryIds.map((id: number) =>
                    call(getEnquiry, { enquiryId: id })
                  )
                );
                const failedEnquiries = sortBy(
                  jobResponse.data.EnquiriesCreateJobs.filter(
                    (e: any) => e.status === 'failed'
                  ),
                  ['id']
                ).reverse();
                const failedEnquiryIds = failedEnquiries.map(
                  (e: any) => e.details.bulkId
                );
                const failedRetry = failedEnquiryIds.reduce(
                  (o: any, id: string) => {
                    o[id] === undefined ? (o[id] = 1) : (o[id] += 1);
                    return o;
                  },
                  {}
                );

                failedFinishRetried = Object.entries(failedRetry)
                  .filter((f: any[]) => f[1] >= 2)
                  .map((f: any[]) => {
                    const failedEnquiry = failedEnquiries.find(
                      (e: any) => e.details.bulkId === f[0]
                    );
                    return {
                      ...sharedInfo,
                      ...failedEnquiry.details
                    };
                  });
                failedMapIds = failedFinishRetried.map((f: any) => f.mapId);
                const pending =
                  totalAmount -
                  (completedEnquiryIds.length + failedFinishRetried.length);
                const results = enquiries
                  .filter((e: any) => e?.data?.id)
                  .map((e: any) => e.data);
                yield put(setBulkBatchCreated(totalAmount - pending));
                yield put(setPendingEquiriesAmount(pending));
                if (results.length > 0) {
                  yield put(updateNewLodgedEquiries(results));
                }
                yield delay(200);
              }
            }
          }
        } while (!finishBulkCreate);
        yield put(setBulkCreating(undefined));
        yield put(setBulkBatchTotal(undefined));
        yield put(setBulkBatchCreated(undefined));
      } else {
        yield put(lodgeNewEnquirySuccess([response.data]));
      }
    } else {
      yield put(lodgeNewEnquiryFailure('Unable to lodge new enquiry'));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      lodgeNewEnquiryFailure(
        typeof msg === 'string' ? msg : 'Unable to lodge new enquiry'
      )
    );
  }
}

function* handleLodgeNewEnquirySentinel(request: any) {
  try {
    let completedEnquiries = [];
    let failedFinishRetried = [];
    let failedMapIds = [];
    let finishBulkCreate = false;
    let firstRound = true;
    let totalAmount = 1;
    let sharedInfo = {};
    if (Array.isArray(request.payload)) {
      totalAmount = request.payload.length;
      sharedInfo = {
        start_at: request.payload[0].CommencesOn,
        end_at: request.payload[0].CompletesOn,
        enquiry_at: request.payload[0].EnquiryDate
      };
      yield put(setBulkBatchTotal(totalAmount));
      yield put(setPendingEquiriesAmount(request.payload.length));
      yield put(setBulkCreating(true));
    }
    const response: AxiosResponse = yield call(
      Array.isArray(request.payload)
        ? bulkCreateEnquiry
        : createEnquirySentinel,
      request.payload
    );
    if (response?.data) {
      if (response.data.system_job_id) {
        do {
          const state: LodgeEnquiryState = yield select();
          const loadedEnquiries = state.enquiries;
          const pendingAmount = state.pendingEnquiriesAmount || 0;
          if (pendingAmount <= 0) {
            finishBulkCreate = true;
            yield put(setSuccessEnquiryUserReference(completedEnquiries));
            yield put(setMapErrorForLodgeEnquiryFail(failedMapIds));
            yield put(setFailedEnquiries(failedFinishRetried));
          } else {
            if (!firstRound) {
              yield delay(2000);
            }
            firstRound = false;
            const jobResponse: AxiosResponse = yield call(
              getSystemJob,
              { type: 'BULK_CREATE_ENQUIRIES' },
              response.data.system_job_id
            );
            if (jobResponse?.data) {
              if (
                Array.isArray(jobResponse.data.EnquiriesCreateJobs) &&
                jobResponse.data.EnquiriesCreateJobs.length > 0
              ) {
                const loadedEnquiryIds = loadedEnquiries.map((e: any) => e.id);
                completedEnquiries =
                  jobResponse.data.EnquiriesCreateJobs.filter(
                    (e: any) => e.status === 'completed'
                  );
                const completedEnquiryIds = completedEnquiries.map(
                  (e: any) => e.details.enquiryId
                );
                const notloadedEnquiryIds = completedEnquiryIds.filter(
                  (id: number) => !loadedEnquiryIds.includes(id)
                );
                const enquiries: any[] = yield all(
                  notloadedEnquiryIds.map((id: number) =>
                    call(getEnquiry, { enquiryId: id })
                  )
                );
                const failedEnquiries = sortBy(
                  jobResponse.data.EnquiriesCreateJobs.filter(
                    (e: any) => e.status === 'failed'
                  ),
                  ['id']
                ).reverse();
                const failedEnquiryIds = failedEnquiries.map(
                  (e: any) => e.details.bulkId
                );
                const failedRetry = failedEnquiryIds.reduce(
                  (o: any, id: string) => {
                    o[id] === undefined ? (o[id] = 1) : (o[id] += 1);
                    return o;
                  },
                  {}
                );

                failedFinishRetried = Object.entries(failedRetry)
                  .filter((f: any[]) => f[1] >= 2)
                  .map((f: any[]) => {
                    const failedEnquiry = failedEnquiries.find(
                      (e: any) => e.details.bulkId === f[0]
                    );
                    return {
                      ...sharedInfo,
                      ...failedEnquiry.details
                    };
                  });
                failedMapIds = failedFinishRetried.map((f: any) => f.mapId);
                const pending =
                  totalAmount -
                  (completedEnquiryIds.length + failedFinishRetried.length);
                const results = enquiries
                  .filter((e: any) => e?.data?.id)
                  .map((e: any) => e.data);
                yield put(setBulkBatchCreated(totalAmount - pending));
                yield put(setPendingEquiriesAmount(pending));
                if (results.length > 0) {
                  yield put(updateNewLodgedEquiries(results));
                }
                yield delay(200);
              }
            }
          }
        } while (!finishBulkCreate);
        yield put(setBulkCreating(undefined));
        yield put(setBulkBatchTotal(undefined));
        yield put(setBulkBatchCreated(undefined));
      } else {
        yield put(lodgeNewEnquirySuccess([response.data]));
      }
    } else {
      yield put(lodgeNewEnquiryFailure('Unable to lodge new enquiry'));
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      lodgeNewEnquiryFailure(
        typeof msg === 'string' ? msg : 'Unable to lodge new enquiry'
      )
    );
  }
}

function* handleUploadEnquiryShape(request: any) {
  try {
    const signResponse: AxiosResponse = yield call(
      uploadEnquiriesShape,
      request.payload.name
    );

    if (!signResponse?.data?.fields || !signResponse?.data?.url) {
      yield put(uploadEnquiryShapeFailure('Unable to get presigned post'));
      return;
    }

    const formData = new FormData();
    Object.entries(signResponse.data.fields).forEach(([key, value]) => {
      if (key === 'bucket') {
        return;
      }
      formData.append(key, String(value));
    });

    formData.append('Content-Type', request.payload.type);
    formData.append('file', request.payload);
    yield call(uploadFileToS3, signResponse.data.url, formData);

    const response: AxiosResponse = yield call(
      parseEnquiriesShape,
      signResponse.data.fields.key
    );
    if (Array.isArray(response?.data)) {
      const geometries = response.data.map((d) => d.geometry);
      const userReferences = response.data.map((d) => {
        let userRef = '';
        if (d.properties?.USER_REF) {
          userRef = String(d.properties?.USER_REF);
          userRef = userRef.trim().substring(0, 50);
        }
        return userRef;
      });
      yield put(uploadEnquiryShapeSuccess({ geometries, userReferences }));
    } else {
      if (response.data.geometry) {
        const userReferences = response.data.properties?.USER_REF || '';
        yield put(
          uploadEnquiryShapeSuccess({
            geometries: [response.data.geometry],
            userReferences
          })
        );
      } else {
        yield put(
          uploadEnquiryShapeFailure(
            'There was an error with the data. Please retry.'
          )
        );
      }
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(uploadEnquiryShapeFailure(msg || 'Unable to upload shape.'));
  }
}

function* handleCheckShapeWithinTeam(request: any) {
  try {
    const { shapes, teamId } = request.payload;
    const shape =
      shapes.length === 1 ? shapes[0] : convertShapesToMultiPolygon(shapes);
    const response: AxiosResponse = yield call(
      checkShapeWithinTeamBoundary,
      teamId,
      shape
    );
    if (response?.data !== undefined) {
      yield put(
        checkShapeWithinTeamSuccess(response.data ? 'within' : 'outside')
      );
    } else {
      yield put(
        checkShapeWithinTeamFailure(
          'Unable to check shape within team boundary'
        )
      );
    }
  } catch (err) {
    const msg = Axios.isAxiosError(err) ? err.response?.data?.msg : undefined;
    yield put(
      checkShapeWithinTeamFailure(
        typeof msg === 'string'
          ? msg
          : 'Unable to check shape within team boundary'
      )
    );
  }
}

function* handleGetDraft() {
  try {
    const response: AxiosResponse = yield call(getEnquiryDraft);
    if (response?.data) {
      yield put(getDraftSuccess(response.data));
      if (response.data.details?.data?.authorities) {
        if (response.data.details.data.authorities.authority?.length) {
          yield put(
            getAssetOwnerCountSuccess(
              response.data.details.data.authorities.authority.length
            )
          );
        }
        yield put(
          setWorkingOnBehalfOfList(response.data.details.data.authorities)
        );
      }
    } else {
      yield put(getDraftFailure());
    }
  } catch (err) {
    yield put(getDraftFailure());
  }
}

function* handleSaveDraft(request: any) {
  try {
    let shapes: any[] = [];
    const details = request.payload.details;
    const teamId = request.payload.teamId;
    const state: LodgeEnquiryState = yield select();
    shapes = state.shapes.map((s, inx) => {
      return {
        id: s.shapeId,
        shape: s.geoJson,
        address: s.address,
        user_reference: s.userReference,
        seq_no: inx,
        project_id: teamId
      };
    });

    const response: AxiosResponse = yield call(saveEnquiryDraft, {
      details,
      shapes
    });
    if (response?.data) {
      yield put(saveDraftSuccess());
    } else {
      yield put(saveDraftFailure());
    }
  } catch (err) {
    yield put(saveDraftFailure());
  }
}

function* handleDiscardDraft() {
  try {
    const response: AxiosResponse = yield call(discardEnquiryDraft);
    if (response?.data) {
      yield put(discardDraftSuccess());
    } else {
      yield put(discardDraftFailure());
    }
  } catch (err) {
    yield put(discardDraftFailure());
  }
}

function* handleGetRestriction() {
  try {
    const response: AxiosResponse = yield call(
      getSelfRestriction,
      'CREATE_ENQUIRY'
    );
    if (response?.data) {
      yield put(getRestrictionSuccess(response.data));
    } else {
      yield put(getRestrictionFailure());
    }
  } catch (err) {
    yield put(getRestrictionFailure());
  }
}

function* watchGetGeocode() {
  yield takeLatest(LodgeEnquiryActionTypes.GET_GEOCODE_START, handleGetGeocode);
}

function* watchQueryRecentLayerExtent() {
  yield takeLatest(
    LodgeEnquiryActionTypes.QUERY_RECENT_LAYER_EXTENT_START,
    handleQueryRecentLayerExtent
  );
}

function* watchGetEnquiryEssentials() {
  if (isSentinel) {
    yield takeLatest(
      LodgeEnquiryActionTypes.GET_ENQUIRY_ESSENTIALS_START,
      handleGetEnquiryEssentialsSentinel
    );
  } else {
    yield takeLatest(
      LodgeEnquiryActionTypes.GET_ENQUIRY_ESSENTIALS_START,
      handleGetEnquiryEssentials
    );
  }
}

function* watchGetAssetOwnerCount() {
  yield takeLatest(
    LodgeEnquiryActionTypes.GET_ASSET_OWNERS_COUNT_START,
    handleGetAssetOwnerCount
  );
}

function* watchGetDuplicateEnquiry() {
  yield takeLatest(
    LodgeEnquiryActionTypes.GET_DUPLICATE_ENQUIRY_START,
    handleGetDuplicateEnquiry
  );
}

function* watchGetLastEnquiry() {
  yield takeLatest(
    LodgeEnquiryActionTypes.GET_LAST_ENQUIRY_START,
    handleGetLastEnquiry
  );
}

function* watchLodgeNewEnquiry() {
  if (isSentinel) {
    yield takeLatest(
      LodgeEnquiryActionTypes.LODGE_NEW_ENQUIRY_START,
      handleLodgeNewEnquirySentinel
    );
  } else {
    yield takeLatest(
      LodgeEnquiryActionTypes.LODGE_NEW_ENQUIRY_START,
      handleLodgeNewEnquiry
    );
  }
}

function* watchUploadEnquiryShape() {
  yield takeLatest(
    LodgeEnquiryActionTypes.UPLOAD_ENQUIRY_SHAPE_START,
    handleUploadEnquiryShape
  );
}

function* watchCheckShapeWithinTeam() {
  yield takeLatest(
    LodgeEnquiryActionTypes.CHECK_SHAPE_WITHIN_TEAM_START,
    handleCheckShapeWithinTeam
  );
}

function* watchGetDraft() {
  yield takeLatest(LodgeEnquiryActionTypes.GET_DRAFT_START, handleGetDraft);
}

function* watchSaveDraft() {
  yield takeLatest(LodgeEnquiryActionTypes.SAVE_DRAFT_START, handleSaveDraft);
}

function* watchDiscardDraft() {
  yield takeLatest(
    LodgeEnquiryActionTypes.DISCARD_DRAFT_START,
    handleDiscardDraft
  );
}

function* watchGetRestriction() {
  yield takeLatest(
    LodgeEnquiryActionTypes.GET_RESTRICTIONS_START,
    handleGetRestriction
  );
}

function* sagas() {
  yield all([
    call(watchGetGeocode),
    call(watchQueryRecentLayerExtent),
    call(watchGetEnquiryEssentials),
    call(watchGetAssetOwnerCount),
    call(watchUploadEnquiryShape),
    call(watchGetDuplicateEnquiry),
    call(watchGetLastEnquiry),
    call(watchLodgeNewEnquiry),
    call(watchCheckShapeWithinTeam),
    call(watchGetRestriction),
    call(watchGetDraft),
    call(watchSaveDraft),
    call(watchDiscardDraft)
  ]);
}

export default sagas;
